import api from '@/shared/api/api';

import type {
  AnalyticsModel,
  AnalyticsModelSummary,
  AnalyticsSection,
  AnalyticsTagsConfig,
} from '@/entities/analyticsModel/config';

import {
  analyticsModelSummaryToDomain,
  analyticsModelToDomain,
  analyticsSectionToWire,
  analyticsTagsToWire,
  cloneResultToDomain,
} from './scribeCatalogConv';
import type { CatalogCloneResult } from './scribeCatalogTypes';
import { callScribe } from './scribeError';
import { fetchAllCatalogPages } from './scribePaging';

// === Request types ===

export type ListAnalyticsModelsParams = {
  organization_id: string;
  language?: string;
  include_draft?: boolean;
  include_deleted?: boolean;
};

export type AnalyticsModelListResponse = {
  items: AnalyticsModelSummary[];
  total: number;
};

export type CreateAnalyticsModelRequest = {
  code: string;
  language: string;
  name: string;
  organization_id: string;
  tags?: AnalyticsTagsConfig;
  sections?: AnalyticsSection[];
};

export type UpdateAnalyticsModelRequest = {
  code?: string;
  language?: string;
  name?: string;
  tags?: AnalyticsTagsConfig;
  sections?: AnalyticsSection[];
};

export type RestoreAnalyticsModelRequest = {
  code?: string;
  name?: string;
};

export type CloneAnalyticsModelRequest = {
  target_organization_id: string;
};

// === API client ===

export const analyticsModelsApi = {
  listAnalyticsModels: (
    params: ListAnalyticsModelsParams,
  ): Promise<AnalyticsModelListResponse> =>
    fetchAllCatalogPages(async (Offset, Limit) => {
      const response = await callScribe(
        api.analyticsModel.list({
          OrganizationID: params.organization_id,
          Language: params.language,
          IncludeDraft: params.include_draft,
          IncludeDeleted: params.include_deleted,
          Offset,
          Limit,
        }),
      );

      return {
        items: response.Models.map(analyticsModelSummaryToDomain),
        total: response.Total,
      };
    }),

  createAnalyticsModel: async (
    data: CreateAnalyticsModelRequest,
  ): Promise<AnalyticsModel> => {
    const response = await callScribe(
      api.analyticsModel.create({
        OrganizationID: data.organization_id,
        Code: data.code,
        Language: data.language,
        Name: data.name,
        Tags: analyticsTagsToWire(data.tags),
        Sections: (data.sections ?? []).map(analyticsSectionToWire),
      }),
    );

    return analyticsModelToDomain(response.Model);
  },

  getAnalyticsModel: async (id: string): Promise<AnalyticsModel> => {
    const response = await callScribe(api.analyticsModel.get({ ModelID: id }));

    return analyticsModelToDomain(response.Model);
  },

  updateAnalyticsModel: async (
    id: string,
    data: UpdateAnalyticsModelRequest,
  ): Promise<AnalyticsModel> => {
    const response = await callScribe(
      api.analyticsModel.update({
        ModelID: id,
        Code: data.code,
        Language: data.language,
        Name: data.name,
        Tags: analyticsTagsToWire(data.tags),
        Sections: data.sections && {
          Sections: data.sections.map(analyticsSectionToWire),
        },
      }),
    );

    return analyticsModelToDomain(response.Model);
  },

  deleteAnalyticsModel: async (id: string): Promise<void> => {
    await callScribe(api.analyticsModel.delete({ ModelID: id }));
  },

  restoreAnalyticsModel: async (
    id: string,
    data?: RestoreAnalyticsModelRequest,
  ): Promise<void> => {
    await callScribe(
      api.analyticsModel.restore({
        ModelID: id,
        Code: data?.code,
        Name: data?.name,
      }),
    );
  },

  cloneAnalyticsModel: async (
    id: string,
    data: CloneAnalyticsModelRequest,
  ): Promise<CatalogCloneResult> => {
    const response = await callScribe(
      api.analyticsModel.clone({
        OrganizationID: data.target_organization_id,
        Overrides: [{ Family: 'analytics_model', SourceAssetID: id }],
      }),
    );

    return cloneResultToDomain(response.Result);
  },
};
